// src/pages/Admin/Adherents/helpers.js

export const CHUNK = 60;

export const STATUS_OPTIONS = [
  "Licencié",
  "Inscrit",
  "Cours d'essai",
  "Licencié - Arrêt",
  "Non Licencié - Arrêt",
  "Licencié Hors Club",
  "Ancien Licencié",
  "Ancien Licencié et Arrêt",
];

export function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.adherents)) return data.adherents;
  }

  return [];
}

export function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getRoleId(user) {
  return Number(user?.role_id || user?.roleId || user?.role?.id || 0);
}

export function formatDate(dateValue) {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function sortIcon(order) {
  if (order === "asc") return "▲";
  if (order === "desc") return "▼";
  return "⇅";
}

export function normalizeText(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function getAdherentId(adherent) {
  return adherent?.id ?? adherent?.adherent_id ?? adherent?.adherentId;
}

export function getAdherentName(adherent) {
  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
}

export function getGroupsText(adherent) {
  const groups = Array.isArray(adherent?.Groups)
    ? adherent.Groups
    : Array.isArray(adherent?.groups)
      ? adherent.groups
      : [];

  return groups.map((group) => group?.nom || group?.name).filter(Boolean).join(", ");
}

export function getUsersText(adherent) {
  const users = Array.isArray(adherent?.Users)
    ? adherent.Users
    : Array.isArray(adherent?.users)
      ? adherent.users
      : [];

  return users
    .map((user) => `${user?.nom || ""} ${user?.prenom || ""}`.trim())
    .filter(Boolean)
    .join(", ");
}

export function countByStatus(adherents, status) {
  return adherents.filter(
    (adherent) => String(adherent?.statut_inscription || "") === String(status)
  ).length;
}

export function countWithoutGroup(adherents) {
  return adherents.filter((adherent) => {
    const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];
    return groups.length === 0;
  }).length;
}

export function countWithValidEmail(adherents) {
  return adherents.filter((adherent) => {
    const email = String(adherent?.email || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }).length;
}

export function getStatusDisplay(status) {
  switch (String(status || "").trim()) {
    case "Licencié":
      return {
        label: "Licencié",
        className:
          "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      };

    case "Inscrit":
      return {
        label: "Inscrit",
        className:
          "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      };

    case "Cours d'essai":
    case "Cours d’essai":
      return {
        label: "Cours d'essai",
        className:
          "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",
      };

    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return {
        label: status,
        className:
          "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      };

    case "Non Licencié - Arrêt":
      return {
        label: status,
        className:
          "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
      };

    case "Licencié Hors Club":
      return {
        label: status,
        className:
          "bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/20",
      };

    case "Ancien Licencié":
      return {
        label: status,
        className:
          "bg-zinc-100 text-zinc-700 ring-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:ring-zinc-700",
      };

    default:
      return {
        label: status || "Non renseigné",
        className:
          "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
      };
  }
}

export function toAbsoluteUploadUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${value}`;
}

export function fallbackAdherentPhoto(genre) {
  return genre === "Féminin"
    ? "/img/judokate_avatar.png"
    : "/img/judoka_avatar.png";
}

export function getAdherentPhoto(adherent) {
  const photo =
    adherent?.photo_url ||
    adherent?.photoURL ||
    adherent?.photo ||
    adherent?.image_url ||
    "";

  if (!photo || photo === "/img/default_avatar.png") {
    return fallbackAdherentPhoto(adherent?.genre);
  }

  return toAbsoluteUploadUrl(photo);
}

export function getPreviousSeason(currentSeason = "") {
  const [y1, y2] = String(currentSeason).split("-").map(Number);
  if (!y1 || !y2) return "";
  return `${y1 - 1}-${y2 - 1}`;
}

export function getNextSeason(currentSeason = "") {
  const [y1, y2] = String(currentSeason).split("-").map(Number);
  if (!y1 || !y2) return "";
  return `${y1 + 1}-${y2 + 1}`;
}

export function neighborsFromSeason(season) {
  const current = String(season || "").trim();
  if (!current) return [];

  const previous = getPreviousSeason(current);
  const next = getNextSeason(current);

  return [previous, current, next].filter(Boolean);
}

export function getGroupPillClasses(group) {
  const color = String(group?.color_groupe || "").trim();

  if (color.startsWith("bg-")) {
    return `${color} text-slate-900 ring-1 ring-black/5 dark:ring-white/10`;
  }

  return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
}

export function parseDateOnlyLocal(value) {
  if (!value) return null;

  const str = String(value).slice(0, 10);
  const [year, month, day] = str.split("-").map(Number);

  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}